import * as React from "react"
import { router } from "@inertiajs/react"
import { Folder as FolderIcon, Plus, Trash2, X } from "lucide-react"
import { Button } from "@ui/Button"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Select } from "@ui/Select"
import { Tabs } from "@ui/Tabs"
import { Textarea } from "@ui/Textarea"
import { cn } from "@lib/cn"
import type { Credential, Environment, Folder, KeyValueEntry, Project } from "@lib/inertia-types"
import { KeyValueEditor } from "./KeyValueEditor"

export interface HttpSettingRow {
  id: number
  owner_type: string
  owner_id: number
  environment_id: number | null
  base_url: string | null
  headers: KeyValueEntry[]
  query_params: KeyValueEntry[]
  auth_type: "none" | "credential" | null
  credential_id: number | null
  options: { timeout_ms?: number; follow_redirects?: boolean }
}

type CredentialSummary = Pick<Credential, "id" | "name" | "kind">

export function FolderSettingsPanel({
  project,
  folder,
  environments,
  settings,
  credentials,
  onClose,
}: {
  project: Project
  folder: Folder
  environments: Environment[] // environments applicable to this folder's service
  settings: HttpSettingRow[] // rows owned by this folder
  credentials: CredentialSummary[]
  onClose: () => void
}) {
  const isService = folder.parent_id === null
  const serviceEnvs = environments.filter((e) => e.folder_id !== null)
  // "defaults" layer + one layer per environment
  const [layerEnvId, setLayerEnvId] = React.useState<number | null>(null)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-cloverhound-gray-200 px-4 py-2 dark:border-cloverhound-gray-850">
        <FolderIcon className="size-4 shrink-0 text-cloverhound-gray-500" aria-hidden="true" />
        <div className="min-w-0">
          <p className="truncate text-small font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
            {folder.name}
          </p>
          <p className="text-micro text-cloverhound-gray-600">
            {isService
              ? "API service — settings inherit down to every request in this folder."
              : "Folder — settings layer on top of the service's."}
          </p>
        </div>
        <Button variant="ghost" size="iconXs" onClick={onClose} aria-label="Close settings" className="ml-auto">
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <Tabs.Root defaultValue="settings">
          <Tabs.List>
            <Tabs.Tab value="settings">Settings</Tabs.Tab>
            {isService && <Tabs.Tab value="environments">Environments</Tabs.Tab>}
            <Tabs.Tab value="docs">Docs</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="settings">
            <div className="mb-4 flex flex-wrap items-center gap-1.5">
              <LayerChip
                label="Defaults"
                active={layerEnvId === null}
                hasContent={settings.some((s) => s.environment_id === null)}
                onClick={() => setLayerEnvId(null)}
              />
              {environments.map((env) => (
                <LayerChip
                  key={env.id}
                  label={env.name}
                  active={layerEnvId === env.id}
                  hasContent={settings.some((s) => s.environment_id === env.id)}
                  onClick={() => setLayerEnvId(env.id)}
                />
              ))}
            </div>
            <SettingsLayerForm
              key={layerEnvId ?? "defaults"}
              project={project}
              folder={folder}
              environmentId={layerEnvId}
              row={settings.find((s) => s.environment_id === layerEnvId) ?? null}
              credentials={credentials}
            />
          </Tabs.Panel>

          {isService && (
            <Tabs.Panel value="environments">
              <ServiceEnvironments project={project} folder={folder} envs={serviceEnvs} />
            </Tabs.Panel>
          )}

          <Tabs.Panel value="docs">
            <FolderDocs project={project} folder={folder} />
          </Tabs.Panel>
        </Tabs.Root>
      </div>
    </div>
  )
}

function LayerChip({
  label,
  active,
  hasContent,
  onClick,
}: {
  label: string
  active: boolean
  hasContent: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-pill px-2.5 py-1 text-mini font-medium transition-colors duration-quick ease-out-quad",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloverhound-indigo-500",
        active
          ? "bg-cloverhound-indigo-600 text-white"
          : "bg-cloverhound-gray-100 text-cloverhound-gray-700 hover:bg-cloverhound-gray-200 dark:bg-cloverhound-gray-850 dark:text-cloverhound-gray-300 dark:hover:bg-cloverhound-gray-800",
      )}
    >
      {label}
      {hasContent && <span className={cn("ml-1", active ? "text-white/80" : "text-cloverhound-indigo-500")}>•</span>}
    </button>
  )
}

function SettingsLayerForm({
  project,
  folder,
  environmentId,
  row,
  credentials,
}: {
  project: Project
  folder: Folder
  environmentId: number | null
  row: HttpSettingRow | null
  credentials: CredentialSummary[]
}) {
  const [baseUrl, setBaseUrl] = React.useState(row?.base_url ?? "")
  const [headers, setHeaders] = React.useState<KeyValueEntry[]>(row?.headers ?? [])
  const [queryParams, setQueryParams] = React.useState<KeyValueEntry[]>(row?.query_params ?? [])
  const [authType, setAuthType] = React.useState<string>(row?.auth_type ?? "inherit")
  const [credentialId, setCredentialId] = React.useState<string>(
    row?.credential_id ? String(row.credential_id) : "",
  )
  const [timeoutMs, setTimeoutMs] = React.useState<string>(
    row?.options?.timeout_ms ? String(row.options.timeout_ms) : "",
  )
  const [saving, setSaving] = React.useState(false)

  function save() {
    setSaving(true)
    router.put(
      `/projects/${project.slug}/folders/${folder.id}/settings`,
      {
        environment_id: environmentId,
        base_url: baseUrl,
        headers: headers.map((h) => ({ ...h })),
        query_params: queryParams.map((q) => ({ ...q })),
        auth_type: authType,
        credential_id: credentialId ? Number(credentialId) : null,
        options: timeoutMs ? { timeout_ms: Number(timeoutMs) } : {},
      } as unknown as Record<string, never>,
      { preserveScroll: true, preserveState: true, only: ["folder_settings"], onFinish: () => setSaving(false) },
    )
  }

  function clearLayer() {
    if (!row) return
    if (!confirm("Remove this settings layer entirely?")) return
    router.delete(
      `/projects/${project.slug}/folders/${folder.id}/settings?environment_id=${environmentId ?? ""}`,
      { preserveScroll: true, preserveState: true, only: ["folder_settings"] },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Field
        label="Base URL"
        description={environmentId === null ? "Applied unless an environment layer overrides it." : "Overrides the default base URL in this environment."}
      >
        <Input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://api.example.com/v1"
          className="font-mono"
        />
      </Field>

      <Field label="Headers">
        <KeyValueEditor entries={headers} onChange={setHeaders} keyPlaceholder="Header-Name" addLabel="Add header" />
      </Field>

      <Field label="Query params">
        <KeyValueEditor entries={queryParams} onChange={setQueryParams} keyPlaceholder="param" addLabel="Add param" />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Auth">
          <Select.Root value={authType} onValueChange={(v) => v && setAuthType(v)}>
            <Select.Trigger aria-label="Auth type">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="inherit">Inherit</Select.Item>
              <Select.Item value="none">No auth</Select.Item>
              <Select.Item value="credential">Credential</Select.Item>
            </Select.Content>
          </Select.Root>
        </Field>
        {authType === "credential" && (
          <Field label="Credential">
            <Select.Root value={credentialId} onValueChange={(v) => v && setCredentialId(v)}>
              <Select.Trigger aria-label="Credential">
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {credentials.map((c) => (
                  <Select.Item key={c.id} value={String(c.id)}>
                    {c.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Field>
        )}
      </div>

      <Field label="Timeout (ms)" description="Blank inherits the default (30000).">
        <Input
          value={timeoutMs}
          onChange={(e) => setTimeoutMs(e.target.value.replace(/\D/g, ""))}
          placeholder="30000"
          className="max-w-40 font-mono"
        />
      </Field>

      <div className="flex items-center justify-between border-t border-cloverhound-gray-250 pt-4 dark:border-cloverhound-gray-800">
        <div>
          {row && (
            <Button variant="ghost" size="xs" onClick={clearLayer} className="text-cloverhound-red-500">
              <Trash2 className="size-3.5" aria-hidden="true" />
              Remove layer
            </Button>
          )}
        </div>
        <Button onClick={save} loading={saving}>
          Save layer
        </Button>
      </div>
    </div>
  )
}

function ServiceEnvironments({
  project,
  folder,
  envs,
}: {
  project: Project
  folder: Folder
  envs: Environment[]
}) {
  const [newName, setNewName] = React.useState("")

  function addEnv(e: React.FormEvent) {
    e.preventDefault()
    if (!newName.trim()) return
    router.post(
      `/projects/${project.slug}/environments`,
      { name: newName, folder_id: folder.id },
      { preserveScroll: true, preserveState: true, only: ["environments"], onFinish: () => setNewName("") },
    )
  }

  function deleteEnv(env: Environment) {
    if (!confirm(`Delete environment "${env.name}"? Its variables and settings layers go with it.`)) return
    router.delete(`/projects/${project.slug}/environments/${env.id}`, {
      preserveScroll: true,
      preserveState: true,
      only: ["environments"],
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-mini text-cloverhound-gray-600">
        Environments for this service. Every environment's mock endpoint is
        live simultaneously at its own URL. Manage variables on the{" "}
        <a href={`/projects/${project.slug}/environments`} className="text-cloverhound-indigo-600 hover:underline dark:text-cloverhound-indigo-300">
          Environments page
        </a>
        .
      </p>
      {envs.map((env) => (
        <div
          key={env.id}
          className="flex items-center justify-between rounded-md border border-cloverhound-gray-250 px-3 py-2 dark:border-cloverhound-gray-800"
        >
          <div className="flex items-center gap-2">
            <span className="font-mono text-mini font-semibold text-cloverhound-indigo-600 dark:text-cloverhound-indigo-300">
              {env.slug}
            </span>
            <span className="text-small text-cloverhound-gray-900 dark:text-cloverhound-gray-50">{env.name}</span>
          </div>
          <Button variant="ghost" size="iconXs" onClick={() => deleteEnv(env)} aria-label={`Delete ${env.name}`}>
            <Trash2 className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      ))}
      <form onSubmit={addEnv} className="flex items-center gap-2">
        <Input
          size="sm"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="qa"
          className="max-w-48"
        />
        <Button type="submit" size="sm" variant="secondary" disabled={!newName.trim()}>
          <Plus className="size-3.5" aria-hidden="true" />
          Add environment
        </Button>
      </form>
    </div>
  )
}

function FolderDocs({ project, folder }: { project: Project; folder: Folder }) {
  const [docs, setDocs] = React.useState(folder.docs ?? "")
  const [saving, setSaving] = React.useState(false)

  function save() {
    setSaving(true)
    router.patch(
      `/projects/${project.slug}/folders/${folder.id}`,
      { docs },
      { preserveScroll: true, preserveState: true, only: ["folders"], onFinish: () => setSaving(false) },
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <Textarea
        value={docs}
        onChange={(e) => setDocs(e.target.value)}
        rows={8}
        placeholder="Describe this API service (markdown supported)…"
        className="font-mono text-mini"
      />
      <div className="flex justify-end">
        <Button onClick={save} loading={saving}>
          Save docs
        </Button>
      </div>
    </div>
  )
}
